import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Loader2, CheckCircle2, XCircle, Calendar } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import AppHeader from "../components/AppHeader";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso; }
}

export default function AdminSubastas() {
  const { token } = useAuth();
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [fixingProv, setFixingProv] = useState(false);
  const [msg, setMsg] = useState("");

  const auth = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/subastas/admin/runs?limit=20`, { headers: auth }),
        axios.get(`${API}/api/subastas/stats`),
      ]);
      setRuns(a.data.runs || []);
      setStats(b.data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const trigger = async () => {
    if (!window.confirm("¿Lanzar scrape manual ahora? Tarda 2-5 min.")) return;
    setTriggering(true);
    setMsg("");
    try {
      await axios.post(`${API}/api/subastas/admin/scrape`, {}, { headers: auth });
      setMsg("Scrape lanzado en background. Refresca en unos minutos.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al lanzar");
    }
    setTriggering(false);
  };

  const reclassify = async () => {
    if (!window.confirm("¿Re-clasificar las subastas marcadas como 'Otros inmuebles'? Tarda 15-20 min en background y mejora el SEO al descargar el detalle de cada una desde el BOE.")) return;
    setReclassifying(true);
    setMsg("");
    try {
      await axios.post(`${API}/api/subastas/admin/reclassify?limit=500`, {}, { headers: auth });
      setMsg("Re-clasificación lanzada en background. Tardará 15-20 min. Puedes cerrar esta página, sigue corriendo en el servidor.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al lanzar");
    }
    setReclassifying(false);
  };

  const reparseAll = async () => {
    if (!window.confirm("¿Re-parsear el detalle BOE de las próximas 400 subastas?\n\nProcesará en orden: primero las que NO tienen detalle aún. Tarda ~5-7 min.\n\nPuedes pulsar este botón varias veces (es idempotente): cada pulsación procesa otras 400 hasta agotar pendientes.")) return;
    setReparsing(true);
    setMsg("");
    try {
      await axios.post(`${API}/api/subastas/admin/reparse-all?limit=400`, {}, { headers: auth });
      setMsg("Tanda de 400 lanzada. Tardará ~5-7 min. Cuando termine, vuelve a pulsar el botón para procesar las siguientes 400. Puedes seguir tu trabajo, sigue corriendo en background.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al lanzar");
    }
    setReparsing(false);
  };

  const cancelReparse = async () => {
    if (!window.confirm("¿Cancelar el re-parseo en curso?\n\nEl trabajo procesado hasta ahora se conserva. Detectará la cancelación en su próxima comprobación (cada ~50 subastas, unos segundos).")) return;
    try {
      const { data } = await axios.post(`${API}/api/subastas/admin/cancel-reparse`, {}, { headers: auth });
      if (data?.cancelled) {
        setMsg(`Cancelación solicitada. El job se detendrá en breve.`);
      } else {
        setMsg("No hay re-parseo en curso ahora mismo.");
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al cancelar");
    }
  };

  const [showAdvanced, setShowAdvanced] = useState(false);

  const fixProvincias = async () => {
    if (!window.confirm("¿Reparar provincia + localidad de TODAS las subastas que ya tienen detalle?\n\nEsta acción es interna (NO descarga del BOE) y arregla los casos donde el ID lleva el código del juzgado/AEAT (ej. R08=Barcelona) pero el bien está en otra provincia. Tarda ~30-60 s.")) return;
    setFixingProv(true);
    setMsg("");
    try {
      const { data } = await axios.post(`${API}/api/subastas/admin/fix-provincias`, {}, { headers: auth });
      setMsg(`Provincias arregladas: ${data?.procesadas || 0} subastas re-indexadas.`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al reparar provincias");
    }
    setFixingProv(false);
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />
      <main className="max-w-6xl mx-auto p-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display font-black text-3xl">Buscador BOE — Scraper</h1>
            <p className="text-sm text-stone-400 mt-1">
              Sincronización automática <strong>diaria a las 08:00 hora Madrid</strong>. Cada scrape descubre nuevas subastas, descarga el detalle de cada bien (dirección, localidad, cargas, coordenadas del Catastro), expande lotes, arregla provincias mal asignadas y re-fetchea hasta 500 subastas con fecha de inicio faltante. Todo en una sola pasada.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={trigger}
              disabled={triggering}
              className="bg-[#E0651F] hover:bg-orange-600 text-white px-5 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="admin-subastas-trigger"
              title="Descubre nuevas + descarga detalles + expande lotes + arregla provincias en una sola pasada."
            >
              {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Lanzar scrape ahora
            </button>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-stone-500 hover:text-stone-200 underline"
              data-testid="admin-subastas-toggle-advanced"
            >
              {showAdvanced ? "Ocultar opciones avanzadas" : "Opciones avanzadas"}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="mb-6 bg-stone-900/60 border border-stone-800 rounded-sm p-4">
            <div className="text-xs text-stone-400 mb-3">
              Estas operaciones rara vez son necesarias. <strong>"Lanzar scrape ahora"</strong> ya hace todo en una sola pasada. Úsalas solo si necesitas reprocesar manualmente datos antiguos.
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={fixProvincias}
                disabled={fixingProv}
                className="bg-emerald-900/40 hover:bg-emerald-900/60 border border-emerald-800 text-emerald-200 px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50 text-sm"
                data-testid="admin-subastas-fix-provincias"
                title="Recalcula provincia, localidad y URL SEO de TODAS las subastas con detalle ya parseado. Sin tocar el BOE — operación interna rápida (~30-60 s)."
              >
                {fixingProv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Reparar provincias (sin BOE, ~30 s)
              </button>
              <button
                onClick={reparseAll}
                disabled={reparsing}
                className="bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-100 px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50 text-sm"
                data-testid="admin-subastas-reparse-all"
                title="Re-descarga el detalle de las próximas 400 subastas del BOE (~5-7 min en background)."
              >
                {reparsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Re-parsear detalle (BOE, 400)
              </button>
              <button
                onClick={reclassify}
                disabled={reclassifying}
                className="bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-100 px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50 text-sm"
                data-testid="admin-subastas-reclassify"
                title="Re-clasifica subastas marcadas como 'Otros inmuebles' (clúster legacy)."
              >
                {reclassifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Mejorar clasificación
              </button>
              <button
                onClick={cancelReparse}
                className="bg-red-900/40 hover:bg-red-900/60 border border-red-800 text-red-200 px-4 py-2 rounded inline-flex items-center gap-2 text-sm"
                data-testid="admin-subastas-cancel-reparse"
                title="Cancelar el re-parseo en curso. Lo procesado hasta ahora se conserva."
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {msg && <div className="mb-6 bg-blue-950/50 border border-blue-900 text-blue-200 p-4 rounded">{msg}</div>}

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard label="Subastas activas" value={stats?.total_activas?.toLocaleString("es-ES") || "—"} />
          <StatCard label="Última actualización" value={fmtDT(stats?.ultima_actualizacion)} />
          <StatCard label="Top provincia" value={stats?.top_provincias?.[0] ? `${stats.top_provincias[0].nombre} (${stats.top_provincias[0].n})` : "—"} />
        </div>

        {/* Top provincias */}
        {stats?.top_provincias?.length > 0 && (
          <div className="bg-stone-900 border border-stone-800 rounded p-5 mb-8">
            <h2 className="font-bold mb-3 text-sm uppercase tracking-wider text-stone-400">Top 10 provincias</h2>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              {stats.top_provincias.map(p => (
                <div key={p.codigo} className="flex justify-between"><span>{p.nombre}</span><span className="text-stone-400">{p.n}</span></div>
              ))}
            </div>
          </div>
        )}

        {/* Runs history */}
        <h2 className="font-bold mb-3 text-sm uppercase tracking-wider text-stone-400 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Historial de ejecuciones
        </h2>
        <div className="bg-stone-900 border border-stone-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-800/50 text-stone-400 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Inicio</th>
                <th className="text-left p-3">Fin</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3">Nuevas</th>
                <th className="text-right p-3">Actualizadas</th>
                <th className="text-right p-3">Desactivadas</th>
                <th className="text-right p-3">Total vistas</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="p-6 text-center text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>}
              {!loading && runs.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-stone-500">Sin ejecuciones aún</td></tr>}
              {runs.map(r => (
                <tr key={r.id} className="border-t border-stone-800">
                  <td className="p-3 text-stone-300">{fmtDT(r.started_at)}</td>
                  <td className="p-3 text-stone-400">{fmtDT(r.finished_at)}</td>
                  <td className="p-3">
                    {r.status === "ok" ? <span className="text-green-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                      : r.status === "running" ? <span className="text-yellow-400 inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> En curso</span>
                      : <span className="text-red-400 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {r.status}</span>}
                  </td>
                  <td className="p-3 text-right text-green-300">{r.summary?.nuevas || 0}</td>
                  <td className="p-3 text-right text-stone-300">{r.summary?.actualizadas || 0}</td>
                  <td className="p-3 text-right text-stone-400">{r.summary?.desactivadas || 0}</td>
                  <td className="p-3 text-right text-stone-300">{r.summary?.total_vistas || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-stone-900 border border-stone-800 rounded p-4">
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">{label}</div>
      <div className="text-2xl font-display font-black text-white">{value}</div>
    </div>
  );
}
